import { useState } from 'react';
import { isQuarterTurn } from '../utils/pageRotation';

// A scanned page turned by its saved rotation (utils/pageRotation.js). CSS
// only — the stored file is never rewritten — shared by every scan viewer so
// they all lay a turned page out the same way.
//
// 0 lays out exactly like the plain element the viewers used before, and 180
// just flips it in place. A quarter turn swaps width and height, which a bare
// `transform` doesn't tell layout about, so:
//   • an image sits in a box whose aspect ratio is the TURNED page's (read
//     from the image once it loads), and is drawn rotated inside it at the
//     width that makes its turned width fill the box — still "fit to width,
//     scroll down", like an unturned page;
//   • a PDF iframe sits in a size container and is sized to the container's
//     height × width (100cqh × 100cqw) before turning, so the turned frame
//     covers the pane exactly. Its own viewer still scrolls along the page's
//     axis — that is the PDF viewer's, not ours to change.
//
// The element tree is the SAME at every angle — a wrapper that is
// `display: contents` (layout-invisible) until a quarter turn makes it the
// box — so turning a page only restyles it. A different tree per angle made
// React rebuild the element on every click: a PDF reloaded and lost its
// place, an image flashed and decoded again.

export function RotatedImg({ src, alt, rotation = 0, className, style, ...rest }) {
  // naturalWidth / naturalHeight of the image at `src`, measured on every
  // load (turned or not, so a first quarter turn needs no hidden frame); kept
  // with its src so paging never borrows the previous page's ratio. `failed`:
  // the image didn't decode — show the browser's broken image, never hide it.
  const [measured, setMeasured] = useState({ src: '', ratio: 0, failed: false });
  const quarter = isQuarterTurn(rotation);
  const mine = measured.src === src;
  const ratio = mine ? measured.ratio : 0;
  const shown = ratio > 0 || (mine && measured.failed);
  const onLoad = (e) => {
    const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
    setMeasured(w > 0 && h > 0 ? { src, ratio: w / h, failed: false } : { src, ratio: 0, failed: true });
  };
  const onError = () => setMeasured({ src, ratio: 0, failed: true });

  let imgStyle = style;
  if (rotation === 180) imgStyle = { ...style, transform: 'rotate(180deg)' };
  if (quarter) {
    imgStyle = {
      ...style,
      position: 'absolute', top: '50%', left: '50%',
      width: ratio ? `${ratio * 100}%` : '100%', maxWidth: 'none', height: 'auto',
      transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
      // Unmeasured, the box has no height yet — keep the page hidden rather
      // than flash it at the wrong size.
      visibility: shown ? 'visible' : 'hidden',
    };
  }
  // A <span>: the viewers put the page inside a <button> (tap for full
  // screen), where a <div> is not allowed.
  return (
    <span className={quarter ? 'rot-img-box' : undefined}
      style={quarter ? (ratio ? { aspectRatio: String(1 / ratio) } : undefined) : { display: 'contents' }}>
      <img src={src} alt={alt} className={className} style={imgStyle} onLoad={onLoad} onError={onError} {...rest} />
    </span>
  );
}

export function RotatedFrame({ src, title, rotation = 0, className, style }) {
  const quarter = isQuarterTurn(rotation);
  let frameStyle = style;
  if (rotation === 180) frameStyle = { ...style, transform: 'rotate(180deg)' };
  if (quarter) {
    frameStyle = {
      ...style,
      position: 'absolute', top: '50%', left: '50%',
      width: '100cqh', height: '100cqw', minHeight: 0,
      transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
    };
  }
  const boxStyle = quarter
    ? (style?.minHeight != null ? { minHeight: style.minHeight } : undefined)
    : { display: 'contents' };
  return (
    <div className={quarter ? 'rot-frame-box' : undefined} style={boxStyle}>
      <iframe src={src} title={title} className={className} style={frameStyle} />
    </div>
  );
}
